/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Fintecy Investors API
 * API for managing investors, accounts, and transactions in the Fintecy platform
 * OpenAPI spec version: 1.0.0
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Error, GetAllGoalsParams, Goal, GoalInput, GoalListResponse } from '.././model';

import { customInstance } from '../../axios-instance';

/**
 * Returns a paginated list of financial goals for the authenticated user
 * @summary Get all goals
 */
export const getAllGoals = (params?: GetAllGoalsParams, signal?: AbortSignal) => {
  return customInstance<GoalListResponse>({ url: `/api/goals`, method: 'GET', params, signal });
};

export const getGetAllGoalsQueryKey = (params?: GetAllGoalsParams) => {
  return [`/api/goals`, ...(params ? [params] : [])] as const;
};

export const getGetAllGoalsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllGoals>>,
  TError = Error,
>(
  params?: GetAllGoalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllGoals>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllGoalsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllGoals>>> = ({ signal }) =>
    getAllGoals(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllGoals>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllGoalsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllGoals>>>;
export type GetAllGoalsQueryError = Error;

export function useGetAllGoals<TData = Awaited<ReturnType<typeof getAllGoals>>, TError = Error>(
  params: undefined | GetAllGoalsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllGoals>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllGoals>>,
          TError,
          Awaited<ReturnType<typeof getAllGoals>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllGoals<TData = Awaited<ReturnType<typeof getAllGoals>>, TError = Error>(
  params?: GetAllGoalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllGoals>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllGoals>>,
          TError,
          Awaited<ReturnType<typeof getAllGoals>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllGoals<TData = Awaited<ReturnType<typeof getAllGoals>>, TError = Error>(
  params?: GetAllGoalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllGoals>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get all goals
 */

export function useGetAllGoals<TData = Awaited<ReturnType<typeof getAllGoals>>, TError = Error>(
  params?: GetAllGoalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllGoals>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllGoalsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a new financial goal for the authenticated user
 * @summary Create a new goal
 */
export const createGoal = (goalInput: GoalInput, signal?: AbortSignal) => {
  return customInstance<Goal>({
    url: `/api/goals`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: goalInput,
    signal,
  });
};

export const getCreateGoalMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createGoal>>,
    TError,
    { data: GoalInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createGoal>>,
  TError,
  { data: GoalInput },
  TContext
> => {
  const mutationKey = ['createGoal'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createGoal>>,
    { data: GoalInput }
  > = (props) => {
    const { data } = props ?? {};

    return createGoal(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateGoalMutationResult = NonNullable<Awaited<ReturnType<typeof createGoal>>>;
export type CreateGoalMutationBody = GoalInput;
export type CreateGoalMutationError = Error;

/**
 * @summary Create a new goal
 */
export const useCreateGoal = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createGoal>>,
      TError,
      { data: GoalInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createGoal>>,
  TError,
  { data: GoalInput },
  TContext
> => {
  const mutationOptions = getCreateGoalMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns a single financial goal by its UUID
 * @summary Get goal by ID
 */
export const getGoal = (id: string, signal?: AbortSignal) => {
  return customInstance<Goal>({ url: `/api/goals/${id}`, method: 'GET', signal });
};

export const getGetGoalQueryKey = (id: string) => {
  return [`/api/goals/${id}`] as const;
};

export const getGetGoalQueryOptions = <TData = Awaited<ReturnType<typeof getGoal>>, TError = Error>(
  id: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGoal>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGoalQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getGoal>>> = ({ signal }) =>
    getGoal(id, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getGoal>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGoalQueryResult = NonNullable<Awaited<ReturnType<typeof getGoal>>>;
export type GetGoalQueryError = Error;

export function useGetGoal<TData = Awaited<ReturnType<typeof getGoal>>, TError = Error>(
  id: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGoal>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGoal>>,
          TError,
          Awaited<ReturnType<typeof getGoal>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGoal<TData = Awaited<ReturnType<typeof getGoal>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGoal>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGoal>>,
          TError,
          Awaited<ReturnType<typeof getGoal>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGoal<TData = Awaited<ReturnType<typeof getGoal>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGoal>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get goal by ID
 */

export function useGetGoal<TData = Awaited<ReturnType<typeof getGoal>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGoal>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetGoalQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates an existing financial goal
 * @summary Update goal
 */
export const updateGoal = (id: string, goalInput: GoalInput) => {
  return customInstance<Goal>({
    url: `/api/goals/${id}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: goalInput,
  });
};

export const getUpdateGoalMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateGoal>>,
    TError,
    { id: string; data: GoalInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateGoal>>,
  TError,
  { id: string; data: GoalInput },
  TContext
> => {
  const mutationKey = ['updateGoal'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateGoal>>,
    { id: string; data: GoalInput }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateGoal(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateGoalMutationResult = NonNullable<Awaited<ReturnType<typeof updateGoal>>>;
export type UpdateGoalMutationBody = GoalInput;
export type UpdateGoalMutationError = Error;

/**
 * @summary Update goal
 */
export const useUpdateGoal = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateGoal>>,
      TError,
      { id: string; data: GoalInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateGoal>>,
  TError,
  { id: string; data: GoalInput },
  TContext
> => {
  const mutationOptions = getUpdateGoalMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes an existing financial goal (soft delete)
 * @summary Delete goal
 */
export const deleteGoal = (id: string) => {
  return customInstance<void>({ url: `/api/goals/${id}`, method: 'DELETE' });
};

export const getDeleteGoalMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteGoal>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteGoal>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['deleteGoal'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteGoal>>, { id: string }> = (
    props
  ) => {
    const { id } = props ?? {};

    return deleteGoal(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteGoalMutationResult = NonNullable<Awaited<ReturnType<typeof deleteGoal>>>;

export type DeleteGoalMutationError = Error;

/**
 * @summary Delete goal
 */
export const useDeleteGoal = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteGoal>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof deleteGoal>>, TError, { id: string }, TContext> => {
  const mutationOptions = getDeleteGoalMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
